import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { gql, useSuspenseQuery } from '@apollo/client';
import { observer } from 'mobx-react';
import { Suspense, useMemo, useState } from 'react';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { useRouteMatch } from 'react-router-dom';
import { BtwLink, BtwTooltip, CardSortingResponse, FullscreenModal, Pin, StepButtons, SummaryTabs, TinyButton, cn, } from '@marvelapp/ballpark-components';
import { QuestionMarkIcon } from '@marvelapp/ballpark-icons';
import { UserTestStatus } from '@marvelapp/core';
import { CardSortingUtils, StepClass, StepUtils, } from '@marvelapp/user-test-creator';
import { useStepContext } from '../../../../utils/stepContext';
import { SummaryStep } from '../SummaryStep';
import { AgreementMatrix } from './AgreementMatrix';
import { CardMetrics } from './CardMetrics';
import { CategoryMetrics } from './CategoryMetrics';
import { DownloadCSV } from './DownloadCSV';
import { SimilarityMatrix } from './SimilarityMatrix';
import { MetricsContext } from './metricsContext';
import { useGetProjectStatus } from './useGetProjectStatus';
import { useQueryCategoryAliases } from './useQueryCategoryAliases';
var Tabs;
(function (Tabs) {
    Tabs["Cards"] = "Cards";
    Tabs["Categories"] = "Categories";
    Tabs["Agreement"] = "Agreement";
    Tabs["Similarity"] = "Similarity";
})(Tabs || (Tabs = {}));
const UNSORTED_CATEGORY_UUID = 'cat_unsorted';
export const SummaryCardSorting = observer(function SummaryCardSorting(_a) {
    var { active, secondary, showAllLink } = _a, rest = __rest(_a, ["active", "secondary", "showAllLink"]);
    const { step } = useStepContext();
    const match = useRouteMatch();
    const projectPk = Number(match.params.id);
    if (!StepUtils.isCardSortingStep(step))
        return null;
    return (_jsx(SummaryStep, Object.assign({ active: active, extraAction: _jsx(DownloadCSV, { projectPk: projectPk, stepUUID: step.uuid }), showAllLink: showAllLink }, rest, { children: _jsx(Suspense, { fallback: "Loading...", children: _jsx(SummaryMetricTabs, Object.assign({ projectPk: projectPk, step: step }, rest)) }) })));
});
const SummaryMetricTabs = observer(function SummaryMetricTabs({ cardAgreement, categoryAgreementAvg, categoryFrequency, categoryTotals, cardSimilarity, categoryPositionAvg, projectPk, step, userTestUUID, }) {
    var _a;
    const { data: { cardsortCustomCategories } = {} } = useQueryCardSortCustomCategories({
        projectPk,
        stepUuid: step.uuid,
        skip: step.className === StepClass.CardSortingClosed,
    });
    const { data: { cardsortCategoryAliases } = {} } = useQueryCategoryAliases({
        projectPk,
        stepUuid: step.uuid,
    });
    const [isBreakdownModalOpen, setIsBreakdownModalOpen] = useState(false);
    const { data: statusData } = useGetProjectStatus(projectPk);
    const userTestStatus = (((_a = statusData === null || statusData === void 0 ? void 0 : statusData.userTest) === null || _a === void 0 ? void 0 : _a.status) ||
        UserTestStatus.Active);
    const categoryAliases = cardsortCategoryAliases;
    const categoryUUIDToCategoryMap = useMemo(() => getCategoryUUIDToCategoryMap({
        categoryAliases,
        cardsortCustomCategories,
        cardAgreement,
        categoryAgreementAvg,
        categoryFrequency,
        categoryTotals,
        step,
    }), [
        categoryAliases,
        cardsortCustomCategories,
        cardAgreement,
        categoryAgreementAvg,
        categoryFrequency,
        categoryTotals,
        step,
    ]);
    const cardUUIDToCardMap = useMemo(() => CardSortingUtils.getCards(step).reduce((acc, curr) => {
        // eslint-disable-next-line no-param-reassign
        acc[curr.uuid] = Object.assign(Object.assign({}, curr), { title: curr.title || '' });
        return acc;
    }, {}), [step]);
    const metricsContext = useMemo(() => ({
        userTestUUID,
        projectPk,
        step,
        cardAgreement,
        categoryAgreementAvg,
        categoryPositionAvg,
        categoryFrequency,
        categoryTotals,
        categoryAliases,
        cardSimilarity,
        categoryUUIDToCategoryMap,
        cardUUIDToCardMap,
        userTestStatus,
    }), [
        userTestUUID,
        projectPk,
        step,
        cardAgreement,
        categoryAgreementAvg,
        categoryPositionAvg,
        categoryFrequency,
        categoryTotals,
        categoryAliases,
        cardSimilarity,
        categoryUUIDToCategoryMap,
        cardUUIDToCardMap,
        userTestStatus,
    ]);
    const numberOfCards = Object.keys(cardUUIDToCardMap).length;
    return (_jsx(MetricsContext.Provider, { value: metricsContext, children: _jsxs(SummaryTabs.Root
        // TODO: Easier to offset the padding before we completely redesign all other summary steps
        , { 
            // TODO: Easier to offset the padding before we completely redesign all other summary steps
            className: "-mb-8 -ml-8 !w-[calc(100%_+_64px)]", defaultValue: Tabs.Categories, "data-testid": "card-metrics", children: [_jsxs(SummaryTabs.List, { children: [_jsx(SummaryTabs.Trigger, { value: Tabs.Categories, "data-testid": "category-metrics-trigger", children: _jsx(FormattedMessage, { id: 'AhjhJ9', defaultMessage: 'Categories' }) }), _jsx(SummaryTabs.Trigger, { value: Tabs.Cards, "data-testid": "card-metrics-trigger", children: _jsx(FormattedMessage, { id: 'NpqbxV', defaultMessage: 'Cards' }) }), _jsx(TabWithHelpTooltip, { testId: "agreement", helpLink: "https://help.ballparkhq.com/en/articles/9833680-analyzing-card-sort-studies#h_229b248362", helpText: _jsx(FormattedMessage, { id: 'jdmE3O', defaultMessage: 'The agreement tab contains a matrix which displays each card and category pairing along with with the individual agreement rates.' }), value: Tabs.Agreement, children: _jsx(FormattedMessage, { id: 'i97ZS0', defaultMessage: 'Agreement' }) }), _jsx(TabWithHelpTooltip, { testId: "similarity", helpLink: "https://help.ballparkhq.com/en/articles/9833680-analyzing-card-sort-studies#h_2db0791318", helpText: _jsx(FormattedMessage, { id: '29nNvL', defaultMessage: 'Similarity is how often two cards were paired in the same categories. Each cell reflects the percentage of times that two cards were paired together by participants.' }), value: Tabs.Similarity, children: _jsx(FormattedMessage, { id: 'sDLTRt', defaultMessage: 'Similarity' }) })] }), _jsx(SummaryTabs.Content, { value: Tabs.Cards, "data-testid": "card-metrics-content", children: _jsx(CardMetrics, {}) }), _jsx(SummaryTabs.Content, { value: Tabs.Categories, "data-testid": "category-metrics-content", children: _jsx(CategoryMetrics, {}) }), _jsx(SummaryTabs.Content, { value: Tabs.Agreement, "data-testid": "agreement-metrics-content", children: _jsx(AgreementMatrix, {}) }), _jsxs(SummaryTabs.Content, { value: Tabs.Similarity, "data-testid": "similarity-metrics-content", children: [_jsx(CardSortingResponse.Root, { type: numberOfCards < 15 ? 'auto' : 'always', children: _jsx("div", { className: "px-8 pb-8", children: !isBreakdownModalOpen && (_jsx("div", { "data-testid": "similarity-matrix", children: _jsx(SimilarityMatrix, {}) })) }) }), _jsx(SummaryTabs.FooterCta, { children: _jsx(FullscreenModal, { centerContent: false, open: isBreakdownModalOpen, onOpenChange: setIsBreakdownModalOpen, trigger: _jsx(StepButtons.ViewBreakdown, {}), children: isBreakdownModalOpen && (_jsx("div", { className: "px-8 pb-8 pt-5", "data-testid": "similarity-matrix-fullscreen", children: _jsx(SimilarityMatrix, {}) })) }) })] })] }) }));
});
const TabWithHelpTooltip = observer(function TabWithHelpTooltip(props) {
    const { helpText, helpLink, testId } = props, rest = __rest(props, ["helpText", "helpLink", "testId"]);
    return (_jsxs("div", { className: "group relative", children: [_jsx(SummaryTabs.Trigger, Object.assign({ "data-testid": `${testId}-metrics-trigger`, className: cn('pr-8', 'group-hover:data-[state=inactive]:bg-gray-400/25', 'group-hover:data-[state=inactive]:ring-gray-400/25') }, rest)), _jsx(Pin.Rc, { className: "pointer-events-none p-2.5", children: _jsx(BtwTooltip, { className: "font-13 max-w-80 leading-relaxed", content: _jsxs(_Fragment, { children: [_jsx("span", { "data-testid": "tooltip-text", children: helpText }), ' ', _jsx(BtwLink.External, { "data-testid": "tooltip-link", variant: "current", href: helpLink, children: _jsx(FormattedMessage, { id: '5LxgVs', defaultMessage: 'Learn more' }) })] }), children: _jsx(TinyButton, { "data-testid": `${testId}-tooltip`, className: cn('pointer-events-auto', 'bg-gray-400/20', 'ring-gray-400/20', 'hover:!bg-gray-400/50', 'hover:!ring-gray-400/50', 'group-hover:bg-gray-400/25', 'group-hover:ring-gray-400/25'), rounded: "full", standaloneIcon: _jsx(QuestionMarkIcon, {}) }) }) })] }));
});
const GET_CARD_SORT_CUSTOM_CATEGORIES = gql `
  query cardsortCustomCategories($testPk: Int!, $stepUuid: String!) {
    cardsortCustomCategories(usertestPk: $testPk, stepUuid: $stepUuid) {
      customCategoryName
      categoryUuid
    }
  }
`;
function useQueryCardSortCustomCategories({ projectPk: testPk, stepUuid, skip = false, }) {
    return useSuspenseQuery(GET_CARD_SORT_CUSTOM_CATEGORIES, {
        variables: { testPk, stepUuid },
        skip,
    });
}
function getCategoryUUIDToCategoryMap({ step, categoryAliases, cardsortCustomCategories, cardAgreement, categoryAgreementAvg, categoryFrequency, categoryTotals, }) {
    const categoryAliasNames = new Set();
    categoryAliases === null || categoryAliases === void 0 ? void 0 : categoryAliases.forEach((alias) => {
        if (!alias)
            return;
        alias.includedCategoryNames.forEach((name) => {
            categoryAliasNames.add(name);
        });
    });
    const categoryUUIDToCategoryMap = CardSortingUtils.getCategories(step).reduce((acc, curr) => {
        const title = curr.title || '';
        if (categoryAliasNames.has(title))
            return acc;
        // eslint-disable-next-line no-param-reassign
        acc[curr.uuid] = Object.assign(Object.assign({}, curr), { title });
        return acc;
    }, {
        [UNSORTED_CATEGORY_UUID]: {
            uuid: UNSORTED_CATEGORY_UUID,
            id: UNSORTED_CATEGORY_UUID,
            title: 'unsorted',
        },
    });
    if (cardsortCustomCategories) {
        const allCategoryUUIDs = new Set();
        [
            ...cardAgreement,
            ...categoryAgreementAvg,
            ...categoryFrequency,
            ...categoryTotals,
        ].forEach((metric) => {
            allCategoryUUIDs.add(metric.categoryUuid);
        });
        cardsortCustomCategories.forEach((customCategory) => {
            if (!customCategory)
                return;
            // The backend returns duplicate categories with the same name but different
            // UUIDs and automatically aggregates categories with the same name under
            // the same metric. Therefore we need to filter out any custom category that
            // is not present in the metrics
            if (!allCategoryUUIDs.has(customCategory.categoryUuid))
                return;
            const { customCategoryName: title, categoryUuid: uuid } = customCategory;
            categoryUUIDToCategoryMap[uuid] = {
                uuid,
                id: uuid,
                title,
            };
        });
    }
    if (categoryAliases) {
        categoryAliases.forEach((alias) => {
            if (!alias)
                return;
            categoryUUIDToCategoryMap[alias.groupUuid] = {
                uuid: alias.groupUuid,
                id: alias.groupUuid,
                title: alias.standardisedAs,
            };
        });
    }
    categoryUUIDToCategoryMap[UNSORTED_CATEGORY_UUID] = {
        uuid: UNSORTED_CATEGORY_UUID,
        id: UNSORTED_CATEGORY_UUID,
        title: 'Unsorted',
    };
    return categoryUUIDToCategoryMap;
}
